<template>
  <div class="ml-12 mr-12 my-2 user-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">User Management</h2>
          <p class="text-white text-opacity-80 mb-0">
            Manage system users, roles, and permissions
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="search"
          label="Search Users"
          clearable
          class="elevation-0"
          @input="debouncedSearch"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Role filter -->
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="roleFilter"
          :items="roleOptions"
          label="Filter by Role"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedSearch"
        />
      </v-col>

      <!-- Status filter -->
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Filter by Status"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedSearch"
        />
      </v-col>

      <!-- Add User button -->
      <v-col cols="12" sm="6" md="4" class="d-flex align-center justify-start">
        <v-btn
                      color="#002060"
          class="text-white"
          @click="$router.push('/add-user')"
          prepend-icon="mdi-plus"
          rounded
          flat
        >
          Add User
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :items-per-page="10"
          :loading="loading"
          style="border-radius: 17px;"
          loading-text="Loading... Please wait"
          class="custom-header elevation-3"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
        >
            <template v-slot:item.role="{ item }">
              <v-chip
                :color="getRoleColor(item.role)"
                text-color="white"
                small
              >
                {{ item.role || 'user' }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <div class="d-flex align-center">
                <v-chip
                  :color="getStatusColor(item.status)"
                  text-color="white"
                  small
                  class="mr-2"
                >
                  {{ getStatusLabel(item.status) }}
                </v-chip>
                <v-icon 
                  v-if="item.status === 'inactive'" 
                  color="error" 
                  size="small"
                  class="ml-1"
                >
                  mdi-account-off
                </v-icon>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn 
                variant="text" 
                @click.stop="viewUser(item)"
                color="#002060"
                size="small"
                class="mr-1"
              >
                View
              </v-btn>
              
                        <v-tooltip :text="item.status === 'active' ? 'Deactivate User' : 'Activate User'" location="top">
            <template v-slot:activator="{ props }">
              <v-btn 
                v-bind="props"
                variant="text" 
                @click.stop="toggleUserStatus(item)"
                :color="item.status === 'active' ? 'success' : 'error'"
                size="small"
                class="mr-1"
              >
                <v-icon v-if="item.status === 'active'">mdi-account-check</v-icon>
                <v-icon v-else>mdi-account-off</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </div>
  </template>
  
  <script>
  import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
  
  export default {
      components: {
    NotificationDialog
  },
  computed: {
    ...mapGetters(['user'])
  },
    data() {
      return {
        showNotification: false,
        notificationType: 'success',
        notificationTitle: '',
        notificationMessage: '',
        search: '',
        loading: false,
        users: [],
        headers: [
          { title: 'Employee ID',    key: 'employeeId',         sortable: true,  align: 'center' },
          { title: 'Name',           key: 'firstName',          sortable: true,  align: 'center' },
          { title: 'Surname',        key: 'surname',            sortable: true,  align: 'center' },
          { title: 'Email',          key: 'email',              sortable: true,  align: 'center' },
          { title: 'Role',           key: 'role',               sortable: true,  align: 'center' },
          { title: 'Status',         key: 'status',             sortable: true,  align: 'center' },
          { title: 'Date Added',     key: 'dateAdded',          sortable: true,  align: 'center' },
          { title: 'Actions',        key: 'actions',            sortable: false, align: 'center' },
        ],
        sortBy: ['firstName'],
        sortDesc: [false],
        roleFilter: null,
        statusFilter: null,
        roleOptions: [
          { title: 'All Roles', value: null },
          { title: 'Admin', value: 'admin' },
          { title: 'Executive', value: 'Executive' },
          { title: 'Employee', value: 'Employee' },
          { title: 'QA Agent', value: 'QA Agent' }
        ],
        statusOptions: [
          { title: 'All Statuses', value: null },
          { title: 'Active', value: 'active' },
          { title: 'Inactive', value: 'inactive' },
          { title: 'Suspended', value: 'suspended' }
        ]
      };
    },
  
    computed: {
      filteredUsers() {
        let filtered = this.users;

        if (this.search) {
          filtered = filtered.filter(user => {
            const matchesSearch = [user.firstName, user.surname, user.email, user.employeeId, user.role, user.status]
                .filter(Boolean)
                .some(f => f.toString().toLowerCase().includes(this.search.toLowerCase()));
            return matchesSearch;
          });
        }

        if (this.roleFilter) {
          filtered = filtered.filter(user => user.role === this.roleFilter);
        }

        if (this.statusFilter) {
          filtered = filtered.filter(user => user.status === this.statusFilter);
        }

        return filtered;
      },
    },
  
    methods: {
      getUsers() {
        this.loading = true;
        // Listen for real-time updates from Firestore - users collection
        if (this.unsubscribeUsers) this.unsubscribeUsers();
        const usersQuery = query(
          collection(db, 'users'), 
          orderBy('dateAdded', 'desc')
        );
        this.unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
          this.users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          this.loading = false;
        }, (error) => {
          console.error('Firestore error:', error);
          this.users = [];
          this.loading = false;
        });
      },
      getRoleColor(role) {
        const colors = {
          admin: 'red',
          Executive: 'purple',
          Employee: 'blue',
          'QA Agent': 'green'
        };
        return colors[role] || 'green';
      },
      getStatusColor(status) {
        const colors = {
          active: 'success',
          inactive: 'error',
          suspended: 'warning'
        };
        return colors[status] || 'success';
      },
      getStatusLabel(status) {
        const labels = {
          active: 'Active',
          inactive: 'Deactivated',
          suspended: 'Suspended'
        };
        return labels[status] || 'Active';
      },
      viewUser(item) {
        console.log('Viewing user from User page:', item);
        this.$router.push({ name: 'ViewUser', params: { id: item.id } });
      },
      async toggleUserStatus(item) {
        try {
          const newStatus = item.status === 'active' ? 'inactive' : 'active';
          await updateDoc(doc(db, 'users', item.id), {
            status: newStatus,
            updatedAt: new Date().toISOString()
          });
                  console.log(`User ${item.id} status updated to ${newStatus}`);
        
        // Log status change activity
        const changedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.statusChanged(changedBy, this.user?.uid, `${item.firstName} ${item.surname}`, newStatus);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = `User status updated to ${this.getStatusLabel(newStatus)} successfully!`;
        this.showNotification = true;
        } catch (error) {
          console.error('Error updating user status:', error);
          
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to update user status. Please try again.';
          this.showNotification = true;
        }
      },

      navigateToAddUser() {
        this.$router.push({ name: 'AddUser' });
      },

      debouncedSearch: debounce(function () {
        this.getUsers();
      }, 300),
    },
  

  
    created() {
      this.getUsers();
    },
    beforeUnmount() {
      if (this.unsubscribeUsers) this.unsubscribeUsers();
    }
  };
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
  
  * {
    font-family: 'Montserrat', sans-serif;
  }
  
  .custom-header {
  background-color: #f2f2f2;
}

.status-toggle-btn {
  transition: all 0.2s ease;
}

.status-toggle-btn:hover {
  transform: scale(1.1);
}

/* Custom tooltip styling */
:deep(.v-tooltip .v-overlay__content) {
  background-color: #002060 !important;
  color: white !important;
  font-size: 0.85rem !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.3) !important;
}
  </style>
  